import { useEffect } from "react";
import { MultiSelect } from "@volca/ui/components/ui/multi-select";
import { useGetApAssignees } from "@/accounts-payable/query";
import type { ApActionType } from "@/accounts-payable/types";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";
import useAuth from "@/core/auth/use-auth";
import {
  actionFilterOptions,
  assigneeFilterOptions,
  shownAssigneeTokens,
  unmatchedAssigneeOptions,
} from "./utils";

export const OwnerAndActionFilters = ({
  businessId,
  actionTypes,
  onActionTypesChange,
  assigneeTokens,
  onAssigneeTokensChange,
}: {
  businessId: string | null;
  actionTypes: ApActionType[];
  onActionTypesChange: (types: ApActionType[]) => void;
  assigneeTokens: string[];
  onAssigneeTokensChange: (tokens: string[]) => void;
}) => {
  const { user } = useAuth();
  const {
    data: people = [],
    isPending,
    isError,
    isSuccess,
  } = useGetApAssignees(businessId);
  const { data: connection } = useGetQuickbooksConnection(businessId);

  const shown = isSuccess
    ? shownAssigneeTokens(assigneeTokens, people, user?.id)
    : assigneeTokens;
  const needsRewrite = shown.join() !== assigneeTokens.join();
  useEffect(() => {
    if (needsRewrite) onAssigneeTokensChange(shown);
  }, [needsRewrite, shown, onAssigneeTokensChange]);

  const assigneeOptions = assigneeFilterOptions(people, user?.id);

  return (
    <>
      <MultiSelect
        variant="filter"
        label="Action"
        placeholder="Any"
        options={actionFilterOptions(
          connection?.status !== "not_connected",
          actionTypes,
        )}
        value={actionTypes}
        onChange={(ids) => onActionTypesChange(ids as ApActionType[])}
      />
      <MultiSelect
        variant="filter"
        label="Assignee"
        placeholder="Anyone"
        loading={isPending}
        options={[
          ...assigneeOptions,
          ...(isSuccess
            ? []
            : unmatchedAssigneeOptions(
                assigneeTokens,
                assigneeOptions,
                user?.id,
                isPending,
              )),
          ...(isError
            ? [{ id: "error", name: "Couldn't load people", disabled: true }]
            : []),
        ]}
        value={assigneeTokens}
        onChange={onAssigneeTokensChange}
      />
    </>
  );
};
